import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Crown, Check, Users, MapPin, HardDrive, ArrowRight, CurrencyInr,
  CalendarBlank, Gauge, Star, CheckCircle, XCircle, Clock
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  trial: { gradient: 'from-orange-500 to-amber-500', light: 'bg-orange-50', text: 'text-orange-600' },
  basic: { gradient: 'from-slate-500 to-slate-600', light: 'bg-slate-50', text: 'text-slate-600' },
  standard: { gradient: 'from-sky-500 to-blue-600', light: 'bg-sky-50', text: 'text-sky-600' },
  professional: { gradient: 'from-violet-500 to-purple-600', light: 'bg-violet-50', text: 'text-violet-600' },
  enterprise: { gradient: 'from-amber-500 to-yellow-500', light: 'bg-amber-50', text: 'text-amber-600' },
};

const getColor = (code) => PLAN_COLORS[code] || PLAN_COLORS.basic;
const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processing, setProcessing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        subscriptionApi.myPlan(),
        subscriptionApi.availablePlans(),
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle PayU callback
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      toast.success('Payment successful! Plan has been upgraded.');
      loadData();
    } else if (status === 'failed') {
      toast.error('Payment failed. Please try again.');
    }
  }, [searchParams, loadData]);

  const handlePayment = async (planCode, gateway) => {
    setProcessing(true);
    try {
      const res = await subscriptionApi.createOrder({
        new_plan_code: planCode,
        payment_gateway: gateway,
        billing_cycle: billingCycle,
      });

      if (res.data.status === 'free_plan_activated') {
        toast.success('Plan activated successfully!');
        setUpgradeDialog(false);
        loadData();
        return;
      }

      if (gateway === 'razorpay') {
        // Load Razorpay checkout
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const options = {
            key: res.data.key_id,
            amount: res.data.amount,
            currency: res.data.currency,
            name: "Care'O Platform",
            description: `Plan: ${res.data.plan_name} (${billingCycle})`,
            order_id: res.data.order_id,
            handler: async function (response) {
              try {
                await subscriptionApi.verifyRazorpay({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                });
                toast.success('Payment verified! Plan upgraded successfully.');
                setUpgradeDialog(false);
                loadData();
              } catch {
                toast.error('Payment verification failed');
              }
            },
            prefill: {
              name: user?.name || '',
              email: user?.email || '',
            },
            theme: { color: '#0EA5E9' },
          };
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      } else if (gateway === 'payu') {
        // Create PayU form and submit
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = res.data.payu_url;
        const fields = {
          key: res.data.key,
          txnid: res.data.txnid,
          amount: res.data.amount,
          productinfo: res.data.productinfo,
          firstname: res.data.firstname,
          email: res.data.email,
          phone: res.data.phone || '',
          surl: res.data.surl,
          furl: res.data.furl,
          hash: res.data.hash,
        };
        Object.entries(fields).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = v;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  const sub = subscription;
  const currentPlan = sub?.current_plan;
  const c = getColor(sub?.plan_code);

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="subscription-title">My Subscription</h1>
          <p className="text-sm text-slate-500 mt-1">{sub?.tenant_name} - Manage your plan and billing</p>
        </div>
        <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white gap-2" onClick={() => setUpgradeDialog(true)} data-testid="change-plan-btn">
          <Crown className="h-4 w-4" /> Change Plan
        </Button>
      </div>

      {/* Current Plan Card */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="current-plan-card">
        <div className={`h-2 bg-gradient-to-r ${c.gradient}`}></div>
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className={`text-2xl font-bold font-['Outfit'] ${c.text}`}>{currentPlan?.name || 'No Plan'}</h2>
                <Badge className={`${c.light} ${c.text} border-0`}>{sub?.plan_code?.toUpperCase()}</Badge>
              </div>
              <p className="text-sm text-slate-500">{currentPlan?.description}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
                {sub?.plan_start_date && (
                  <span className="flex items-center gap-1">
                    <CalendarBlank className="h-4 w-4 text-slate-400" />
                    Start: {new Date(sub.plan_start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                )}
                {sub?.plan_end_date && (
                  <span className="flex items-center gap-1">
                    <CalendarBlank className="h-4 w-4 text-slate-400" />
                    Expires: {new Date(sub.plan_end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-800 font-['Outfit']">{formatCurrency(currentPlan?.monthly_price)}</p>
              <p className="text-xs text-slate-400">/month</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="usage-cards">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Users</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">
                  {sub?.usage?.users}<span className="text-sm text-slate-400 font-normal">/{sub?.usage?.max_users}</span>
                </p>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-sky-500 h-1.5 rounded-full" style={{ width: `${Math.min((sub?.usage?.users / sub?.usage?.max_users) * 100, 100)}%` }}></div>
                </div>
              </div>
              <Users className="h-8 w-8 text-sky-400" weight="duotone" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Branches</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">
                  {sub?.usage?.branches}<span className="text-sm text-slate-400 font-normal">/{sub?.usage?.max_branches}</span>
                </p>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-violet-500 h-1.5 rounded-full" style={{ width: `${Math.min((sub?.usage?.branches / sub?.usage?.max_branches) * 100, 100)}%` }}></div>
                </div>
              </div>
              <MapPin className="h-8 w-8 text-violet-400" weight="duotone" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment History */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="payment-history">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 font-['Outfit']">Payment History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Date</TableHead>
                <TableHead className="text-xs">Plan</TableHead>
                <TableHead className="text-xs">Amount</TableHead>
                <TableHead className="text-xs">Cycle</TableHead>
                <TableHead className="text-xs">Gateway</TableHead>
                <TableHead className="text-xs">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(sub?.payment_history || []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">{new Date(p.created_at).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell className="text-sm font-medium">{p.plan_name}</TableCell>
                  <TableCell className="text-sm">{formatCurrency(p.amount)}</TableCell>
                  <TableCell className="text-xs capitalize">{p.billing_cycle}</TableCell>
                  <TableCell className="text-xs capitalize">{p.gateway}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 text-xs ${
                      p.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      p.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {p.status === 'completed' ? <CheckCircle className="h-3 w-3 mr-1" /> : p.status === 'pending' ? <Clock className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                      {p.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {(sub?.payment_history || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No payment history yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upgrade/Change Plan Dialog */}
      <Dialog open={upgradeDialog} onOpenChange={setUpgradeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Choose a Plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-3 py-2">
              <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-sky-500 text-white shadow-md' : 'bg-slate-100 text-slate-600'}`}
                onClick={() => setBillingCycle('monthly')} data-testid="billing-monthly">Monthly</button>
              <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-sky-500 text-white shadow-md' : 'bg-slate-100 text-slate-600'}`}
                onClick={() => setBillingCycle('annual')} data-testid="billing-annual">
                Annual <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] ml-1">Save ~17%</Badge>
              </button>
            </div>

            {/* Plan Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {plans.map(plan => {
                const pc = getColor(plan.code);
                const isCurrentPlan = plan.code === sub?.plan_code;
                const price = billingCycle === 'annual' ? plan.annual_price : plan.monthly_price;
                return (
                  <Card key={plan.id} className={`border ${isCurrentPlan ? 'border-sky-400 ring-2 ring-sky-200' : 'border-slate-200'} relative`} data-testid={`upgrade-plan-${plan.code}`}>
                    {plan.is_popular && <div className="absolute -top-2 left-1/2 -translate-x-1/2"><Badge className="bg-amber-500 text-white border-0 text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" weight="fill" />Popular</Badge></div>}
                    <div className={`h-1 bg-gradient-to-r ${pc.gradient}`}></div>
                    <CardContent className="pt-4 pb-4 space-y-3">
                      <div>
                        <h3 className={`font-semibold font-['Outfit'] ${pc.text}`}>{plan.name}</h3>
                        <p className="text-[10px] text-slate-400 mt-0.5">{plan.description}</p>
                      </div>
                      <div>
                        <span className="text-xl font-bold text-slate-800 font-['Outfit']">{formatCurrency(price)}</span>
                        <span className="text-xs text-slate-400">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] text-slate-500"><Users className="inline h-3 w-3 mr-1" />{plan.max_users} users</p>
                        <p className="text-[10px] text-slate-500"><MapPin className="inline h-3 w-3 mr-1" />{plan.max_branches} branches</p>
                      </div>
                      {isCurrentPlan ? (
                        <Badge className="w-full justify-center bg-sky-100 text-sky-700 border-0 py-1">Current Plan</Badge>
                      ) : (
                        <div className="space-y-1.5">
                          <Button size="sm" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white text-xs h-8"
                            disabled={processing}
                            onClick={() => handlePayment(plan.code, 'razorpay')}
                            data-testid={`pay-razorpay-${plan.code}`}>
                            {processing ? '...' : 'Pay via Razorpay'}
                          </Button>
                          <Button size="sm" variant="outline" className="w-full text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            disabled={processing}
                            onClick={() => handlePayment(plan.code, 'payu')}
                            data-testid={`pay-payu-${plan.code}`}>
                            {processing ? '...' : 'Pay via PayU'}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
